import { SPINE_REGIONS } from "@/data/spine";

// [y, x] control points of the side-view S-curve (front of the body is to the right).
const CTRL = [[18, 78], [70, 94], [122, 80], [220, 58], [318, 76], [372, 98], [424, 88], [470, 64], [512, 70]];

const xAt = (y) => {
  for (let i = 0; i < CTRL.length - 1; i++) {
    const [y0, x0] = CTRL[i];
    const [y1, x1] = CTRL[i + 1];
    if (y <= y1) {
      const t = Math.max(0, (y - y0) / (y1 - y0));
      return x0 + ((x1 - x0) * (1 - Math.cos(Math.PI * t))) / 2;
    }
  }
  return CTRL[CTRL.length - 1][1];
};
const tilt = (y) => (-Math.atan((xAt(y + 2) - xAt(y - 2)) / 4) * 180) / Math.PI;

const build = (region, n, y0, y1, w0, w1, h) =>
  Array.from({ length: n }, (_, i) => {
    const y = y0 + ((y1 - y0) * i) / (n - 1);
    return { region, key: `${region}-${i}`, y, x: xAt(y), w: w0 + ((w1 - w0) * i) / (n - 1), h, rot: tilt(y) };
  });

const VERTEBRAE = [...build("neck", 7, 30, 116, 22, 30, 10), ...build("upper", 12, 134, 314, 32, 42, 12), ...build("lower", 5, 334, 418, 46, 54, 15)];
const SX = xAt(452);
const SACRUM = `M${SX - 30} 432 Q${SX - 2} 424 ${SX + 26} 434 L${SX + 6} 484 Q${SX - 4} 494 ${SX - 12} 482 Z`;
const COCCYX = [496, 504, 511].map((y, i) => ({ y, x: xAt(y) - 2 + i, r: 4 - i }));
const CORD = `M${Array.from({ length: 49 }, (_, i) => `${(xAt(20 + i * 10) - 8).toFixed(1)} ${20 + i * 10}`).join(" L")}`;
const ZONES = { neck: [14, 125, 74], upper: [125, 324, 224], lower: [324, 426, 376], pelvis: [426, 516, 462] };

const Vertebra = ({ v, fill, stroke }) => (
  <g transform={`translate(${v.x.toFixed(1)} ${v.y.toFixed(1)}) rotate(${v.rot.toFixed(1)})`}>
    <rect x={-v.w / 2 - 11} y={-v.h * 0.22} width="13" height={v.h * 0.44} rx="2.5" fill={fill} stroke={stroke} strokeWidth="1" />
    <rect x={-v.w / 2} y={-v.h / 2} width={v.w} height={v.h} rx={v.h / 2.4} fill={fill} stroke={stroke} strokeWidth="1.5" />
  </g>
);

const Region = ({ r, on, onPick }) => {
  const fill = on ? r.color : r.soft;
  const stroke = on ? "#ffffff" : r.color;
  const [y0, y1] = ZONES[r.id];
  return (
    <g
      data-testid={`spine-svg-region-${r.id}`}
      data-active={on ? "true" : "false"}
      onMouseEnter={() => onPick(r.id)}
      onClick={() => onPick(r.id)}
      className="cursor-pointer"
      filter={on ? "url(#spine-glow)" : undefined}
    >
      <rect x="0" y={y0} width="160" height={y1 - y0} fill="transparent" />
      {r.id === "pelvis" ? (
        <>
          <path d={SACRUM} fill={fill} stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
          {COCCYX.map((c) => <circle key={c.y} cx={c.x} cy={c.y} r={c.r} fill={fill} stroke={stroke} strokeWidth="1" />)}
        </>
      ) : (
        VERTEBRAE.filter((v) => v.region === r.id).map((v) => <Vertebra key={v.key} v={v} fill={fill} stroke={stroke} />)
      )}
    </g>
  );
};

const Marker = ({ r }) => {
  const cy = ZONES[r.id][2];
  const base = xAt(cy) + 30;
  const cx = Math.min(148, base + 14);
  return (
    <g pointerEvents="none">
      <line x1={base} y1={cy} x2={cx - 7} y2={cy} stroke={r.color} strokeWidth="2" strokeDasharray="3 3" />
      <circle cx={cx} cy={cy} r="9" fill={r.color} opacity="0.5" className="animate-pulse-ring" style={{ transformBox: "fill-box", transformOrigin: "center" }} />
      <circle cx={cx} cy={cy} r="6" fill={r.color} stroke="#ffffff" strokeWidth="2.5" />
    </g>
  );
};

export const SpineSvg = ({ active, onPick }) => {
  const current = SPINE_REGIONS.find((x) => x.id === active);
  return (
    <svg viewBox="0 0 160 520" className="h-[290px] w-full sm:h-[390px]" aria-hidden="true" focusable="false" data-testid="spine-svg">
      <defs>
        <filter id="spine-glow" x="-40%" y="-20%" width="180%" height="140%">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path d={CORD} fill="none" stroke="#CBD5E1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      {SPINE_REGIONS.map((x) => <Region key={x.id} r={x} on={x.id === active} onPick={onPick} />)}
      <Marker r={current} />
    </svg>
  );
};
